import styled from 'styled-components';
import AddIcon from '../../../new-components/svg-icons/AddIcon';
import MinusIcon from '../../../new-components/svg-icons/MinusIcon';
import { useState } from 'react';
import { logEvent } from '../../../new-lib/analytics';
import { useTranslation } from 'next-i18next';
import { FAQ } from '../../../new-lib/types/common';

interface FAQsProps {
  faqs: FAQ[];
  pageLocation: string;
  id?: number;
  name?: string;
}

const FAQs: React.FC<FAQsProps> = ({ faqs, id, name, pageLocation }) => {
  const { t } = useTranslation('common');
  const [selectedFAQ, setSelectedFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    const faqEventType = selectedFAQ === index ? 'FAQ_collapse' : 'FAQ_expand';
    const eventData =
      pageLocation === 'CompoundPage'
        ? {
            cmpnd_name: name,
            cmpnd_id: id
          }
        : { area_name: name, area_id: id };

    logEvent.click(faqEventType, pageLocation, 'FAQs', eventData);
    setSelectedFAQ(prevIndex => (prevIndex === index ? null : index));
  };

  return (
    <Container data-test="faqs" className="faqs">
      <h2 className="headline">{t('faqs')}</h2>
      <div className="faqs-container">
        {faqs
          .filter(faq => faq.question && faq.answer)
          .map((faq, index) => (
            <div key={index} className="faq-content">
              <div
                className="question-container"
                onClick={() => toggleFAQ(index)}
              >
                <h3>{faq.question}</h3>
                <div className="collapse-icon">
                  {selectedFAQ === index ? (
                    <MinusIcon stroke={'#828FA1'} width="10" />
                  ) : (
                    <AddIcon stroke={'#828FA1'} width="10" />
                  )}
                </div>
              </div>
              <div
                className={`faq-answer ${
                  selectedFAQ === index ? 'expanded' : ''
                }`}
              >
                <h4>{faq.answer}</h4>
              </div>
            </div>
          ))}
      </div>
    </Container>
  );
};

const Container = styled.div`
  h2 {
    color: #00324c;
    font-family: materialBold;
    font-size: 24px;
    text-transform: capitalize;
    margin-bottom: 8px;
  }
  .faq-content {
    display: flex;
    flex-direction: column;
    padding: 8px 0px;
    gap: 4px;
    border-bottom: 0.5px solid #d3dbe6;

    .faq-answer {
      max-height: 0;
      opacity: 0;
      overflow: hidden;
      transition:
        max-height 0.4s ease,
        opacity 0.4s ease,
        transform 0.4s ease;
      transform: scaleY(0.95);

      &.expanded {
        max-height: 500px;
        opacity: 1;
        transform: scaleY(1);
      }
    }

    .question-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      z-index: 999;
      h3 {
        color: #1a2028;
        font-family: materialSemiBold;
        font-size: 14px;
      }
      .collapse-icon {
        width: 24px;
        height: 24px;
        background: #fbfcfe;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
      }
    }

    h4 {
      color: #5b6573;
      font-family: materialRegular;
      font-size: 14px;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.max}) {
    h2 {
      color: #0c314e;
      font-family: materialSemiBold;
      font-size: 18px;
    }

    .faqs-container {
      padding: 16px;
      border-radius: 8px;
      background: #f3f5f8;
    }
  }
`;

export default FAQs;
